import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Edit, Upload, X } from "lucide-react";
import { AuthContext } from "../App";
import { api } from "../lib/api";

const TABS = ["Films", "Videos", "News", "Submissions"];
const ENDPOINTS = { Films: "/films", Videos: "/songs", News: "/news", Submissions: "/admin/submissions" };

export default function AdminPage() {
  const { user, loading, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tab, setTab] = useState("Films");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => { document.title = "Admin — Je Ke Pictures"; }, []);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/admin/login");
  }, [loading, user, navigate]);

  const endpoint = () => ENDPOINTS[tab];

  const reload = async () => {
    try {
      const res = await api.get(endpoint());
      setItems(res.data);
    } catch (e) {
      toast.error("Failed to load");
    }
  };

  useEffect(() => {
    if (user?.is_admin) reload();
    setEditing(null); setCreating(false);
  }, [tab, user]);

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`${endpoint()}/${id}`);
      toast.success("Deleted");
      reload();
    } catch { toast.error("Delete failed"); }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
    navigate("/admin/login");
  };

  if (loading || !user?.is_admin) {
    return <div className="min-h-screen flex items-center justify-center text-[color:var(--jk-muted)]" data-testid="admin-loading">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-page">
      <header className="border-b border-white/5 sticky top-0 z-40 bg-black/80 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/brand/logo.jpeg" alt="Je Ke Pictures" className="w-10 h-10 rounded-md object-contain bg-white p-0.5" />
            <div>
              <div className="font-cinzel font-bold tracking-[0.2em] text-sm">ADMIN</div>
              <div className="text-xs text-[color:var(--jk-muted)]">{user.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="jk-link text-xs" data-testid="admin-view-site">View Site</a>
            <button onClick={logout} className="jk-btn text-xs" data-testid="admin-logout-btn"><LogOut size={14} /> LOGOUT</button>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 flex gap-6 overflow-x-auto">
          {TABS.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`py-3 font-cinzel text-xs tracking-[0.3em] uppercase whitespace-nowrap border-b-2 transition-colors ${tab === t ? "border-[color:var(--jk-gold)] text-[color:var(--jk-gold)]" : "border-transparent text-[color:var(--jk-muted)] hover:text-white"}`}
              data-testid={`admin-tab-${t.toLowerCase()}`}
            >{t}</button>
          ))}
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-cinzel font-bold text-2xl">{tab}</h2>
          {tab !== "Submissions" && (
            <button onClick={() => { setCreating(true); setEditing({}); }} className="jk-btn jk-btn-solid text-xs" data-testid={`admin-new-${tab.toLowerCase()}`}>
              <Plus size={14}/> NEW
            </button>
          )}
        </div>

        <div className="border border-white/5" data-testid="admin-items-list">
          {items.length === 0 && <div className="p-8 text-[color:var(--jk-muted)] text-sm">No items yet.</div>}
          {items.map((it) => (
            <div key={it.id} className="grid grid-cols-12 gap-4 items-center p-4 border-b border-white/5 last:border-b-0 hover:bg-white/[0.02]" data-testid={`admin-item-${it.id}`}>
              <div className="col-span-8">
                <div className="font-cinzel font-bold text-sm">{it.title || it.subject || it.name}</div>
                <div className="text-xs text-[color:var(--jk-muted)] mt-1">
                  {tab === "Films" && `${it.year || ""} · ${it.status || ""}`}
                  {tab === "Videos" && `${it.category || "Song"} · ${it.film_title || ""}${it.video_url ? " · ✓ video" : " · no video"}`}
                  {tab === "News" && `${it.category || ""}`}
                  {tab === "Submissions" && `${it.email} · ${new Date(it.created_at).toLocaleString()}`}
                </div>
                {tab === "Submissions" && (
                  <div className="text-sm text-white/70 mt-2 whitespace-pre-wrap">{it.message}</div>
                )}
              </div>
              <div className="col-span-4 flex justify-end gap-2">
                {tab !== "Submissions" && (
                  <button onClick={() => { setEditing(it); setCreating(false); }} className="jk-btn text-xs" data-testid={`admin-edit-${it.id}`}><Edit size={12}/>EDIT</button>
                )}
                {tab !== "Submissions" && (
                  <button onClick={() => remove(it.id)} className="jk-btn text-xs border-red-900 text-red-400 hover:bg-red-900 hover:text-white" data-testid={`admin-delete-${it.id}`}><Trash2 size={12}/></button>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>

      {editing !== null && tab !== "Submissions" && (
        <EditorModal
          tab={tab}
          item={editing}
          isNew={creating}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { reload(); setEditing(null); setCreating(false); }}
        />
      )}
    </div>
  );
}

function UploadField({ label, field, accept, value, isVideo, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => setProgress(Math.round((ev.loaded * 100) / (ev.total || 1))),
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      onUploaded(field, url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div>
      <label className="block text-[0.65rem] tracking-[0.3em] font-cinzel text-[color:var(--jk-gold)] uppercase mb-2">{label}</label>
      {value && (
        isVideo
          ? <video src={value} controls className="w-full max-h-56 bg-black mb-2 ring-1 ring-white/10" />
          : <img src={value} alt="preview" className="max-h-40 mb-2 ring-1 ring-white/10 object-contain" />
      )}
      <label className="inline-flex items-center gap-2 text-xs text-[color:var(--jk-gold)] cursor-pointer hover:underline">
        <Upload size={12}/> {uploading ? `Uploading… ${progress}%` : value ? "Replace file" : "Upload file"}
        <input type="file" accept={accept} onChange={upload} className="hidden" data-testid={`admin-upload-${field}`}/>
      </label>
    </div>
  );
}

function EditorModal({ tab, item, isNew, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [saving, setSaving] = useState(false);

  const setField = (field, val) => setForm((f) => ({ ...f, [field]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const endpoint = ENDPOINTS[tab];
      const payload = { ...form };
      if (typeof payload.cast === "string") payload.cast = payload.cast.split(",").map(s => s.trim()).filter(Boolean);
      if (typeof payload.crew === "string") payload.crew = payload.crew.split(",").map(s => s.trim()).filter(Boolean);
      if (payload.year) payload.year = parseInt(payload.year) || undefined;
      if (payload.order !== undefined && payload.order !== "") payload.order = parseInt(payload.order) || 0;

      if (isNew) await api.post(endpoint, payload);
      else await api.put(`${endpoint}/${item.id}`, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const fields = {
    Films: [
      ["title", "Title *", "text"], ["title_hindi", "Title (Hindi)", "text"], ["year", "Year", "number"],
      ["director", "Director", "text"], ["producer", "Producer", "text"], ["status", "Status", "select", ["released", "upcoming"]],
      ["trailer_youtube_id", "YouTube Trailer ID", "text"],
      ["cast", "Cast (comma-separated)", "text"], ["crew", "Crew (comma-separated)", "text"],
      ["synopsis", "Synopsis", "textarea"], ["is_featured", "Featured on Home", "checkbox"],
      ["order", "Sort Order", "number"],
    ],
    Videos: [
      ["title", "Title (English) *", "text"], ["title_hindi", "Title (Hindi)", "text"],
      ["film_title", "Film Title", "text"],
      ["category", "Category", "select", ["Full Movie Song", "Sohar Geet", "Swagat Geet", "Trailer", "Song"]],
      ["singer", "Singer", "text"], ["lyricist", "Lyricist", "text"], ["composer", "Composer", "text"],
      ["youtube_id", "YouTube ID (optional fallback)", "text"],
      ["order", "Sort Order", "number"],
    ],
    News: [
      ["title", "Title *", "text"], ["category", "Category", "select", ["announcement", "press", "coverage"]],
      ["source_url", "Source URL", "text"], ["image_url", "Image URL", "text"],
      ["excerpt", "Excerpt", "textarea"], ["body", "Body", "textarea"],
    ],
  }[tab];

  const asVal = (v) => Array.isArray(v) ? v.join(", ") : (v ?? "");

  return (
    <div className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-lg flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="admin-editor-modal">
      <div className="jk-glass w-full max-w-2xl p-8 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-cinzel font-bold text-xl">{isNew ? `New ${tab.slice(0,-1)}` : `Edit ${tab.slice(0,-1)}`}</h3>
          <button onClick={onClose} data-testid="admin-editor-close"><X /></button>
        </div>
        <div className="space-y-4">
          {tab === "Videos" && (
            <UploadField label="Video File (.mp4) — plays on the site" field="video_url" accept="video/*" value={form.video_url} isVideo onUploaded={setField} />
          )}
          {(tab === "Videos" || tab === "Films") && (
            <UploadField label={tab === "Films" ? "Poster Image (portrait — used on Films grid)" : "Poster / Thumbnail Image"} field="poster_url" accept="image/*" value={form.poster_url} onUploaded={setField} />
          )}
          {tab === "Films" && (
            <UploadField label="Banner Image (landscape — used as Home hero)" field="banner_url" accept="image/*" value={form.banner_url} onUploaded={setField} />
          )}
          {fields.map(([key, label, type, opts]) => (
            <div key={key}>
              <label className="block text-[0.65rem] tracking-[0.3em] font-cinzel text-[color:var(--jk-gold)] uppercase mb-2">{label}</label>
              {type === "textarea" ? (
                <textarea rows={4} value={asVal(form[key])} onChange={(e) => setField(key, e.target.value)} className="jk-input resize-none" data-testid={`admin-field-${key}`}/>
              ) : type === "select" ? (
                <select value={asVal(form[key])} onChange={(e) => setField(key, e.target.value)} className="jk-input" data-testid={`admin-field-${key}`}>
                  <option value="">—</option>
                  {opts.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : type === "checkbox" ? (
                <input type="checkbox" checked={!!form[key]} onChange={(e) => setField(key, e.target.checked)} data-testid={`admin-field-${key}`}/>
              ) : (
                <input type={type} value={asVal(form[key])} onChange={(e) => setField(key, e.target.value)} className="jk-input" data-testid={`admin-field-${key}`}/>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-3 mt-8">
          <button onClick={onClose} className="jk-btn text-xs">CANCEL</button>
          <button onClick={save} disabled={saving} className="jk-btn jk-btn-solid text-xs disabled:opacity-50" data-testid="admin-save-btn">
            {saving ? "SAVING..." : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
}
